import {
  AuthUser,
  getCurrentUser,
  signIn,
  signOut
} from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify'
import { Hub, sessionStorage } from 'aws-amplify/utils'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import type { FC, ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { useDispatch } from 'react-redux'
import { userPool } from '../userpool'
import { useAuth } from '../hooks/useAuth'
import { logger } from '../utils'

interface AuthContextValues {
  getAuthenticatedUser: () => Promise<AuthUser | null>
  signin: (email: string, password: string) => Promise<any>
  signout: () => Promise<any>
}

const AuthContext = createContext<AuthContextValues>({
  getAuthenticatedUser: async () => await new Promise(() => {}),
  signin: async (_email, _password) => await new Promise(() => {}),
  signout: async () => await new Promise(() => {})
})

interface AuthContextProviderProps {
  children: ReactNode
}

export const AuthContextProvider: FC<AuthContextProviderProps> = ({
  children
}) => {
  const [authChannel, setAuthChannel] = useState<any>()
  const [configured, setConfigured] = useState(false) // Used to track when Amplify configuration has completed.
  const { setUserProfile } = useAuth()
  const dispatch = useDispatch()

  useEffect(() => {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: userPool.getUserPoolId(),
          userPoolClientId: userPool.getClientId()
        }
      }
    })

    cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage)

    const channel = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          logger.log('user has been signedIn successfully.')
          break
        case 'signedOut':
          logger.log('user has been signedOut successfully.')
          break
        case 'tokenRefresh':
          logger.log('auth tokens have been refreshed.')
          break
        case 'tokenRefresh_failure':
          logger.error('failure while refreshing auth tokens.')
          break
      }

      setAuthChannel(channel)
    })

    setConfigured(true)

    return function cleanup () {
      channel()
    }
  }, [authChannel])

  /**
   * Attempts to retrieve the authenticated user from Cognito.
   * @returns the authenticated user, or null if there is no authenticated user.
   */
  const getAuthenticatedUser = async (): Promise<AuthUser | null> => {
    let user
    try {
      user = await getCurrentUser()
    } catch (error) {
      user = null
    }
    return user
  }

  const signin = useCallback(async (email: string, password: string) => {
    return await signIn({
      username: email,
      password
    })
  }, [])

  const signout = useCallback(async () => {
    dispatch({ type: 'RESET' })
    setUserProfile(null)
    return await signOut()
  }, [dispatch, setUserProfile])

  const value = {
    getAuthenticatedUser,
    signin,
    signout
  }

  if (configured) {
    return (
      <AuthContext.Provider value={value}>
        {children}
      </AuthContext.Provider>
    )
  } else {
    return null
  }
}

export const useAuthContext = (): AuthContextValues => useContext(AuthContext)
